
import React, { useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { useExhibitData } from '@/hooks/useExhibitData';
import { useAnalyticsTracking } from '@/hooks/useAnalyticsTracking';
import { useExhibitNavigation } from '@/hooks/useExhibitNavigation';
import ExhibitImage from '@/components/exhibit/ExhibitImage';
import ExhibitAudio from '@/components/exhibit/ExhibitAudio';
import ExhibitVideo from '@/components/exhibit/ExhibitVideo';
import ExhibitNavigation from '@/components/exhibit/ExhibitNavigation';

import ExhibitDetails from '@/components/exhibit/ExhibitDetails';
import { Skeleton } from "@/components/ui/skeleton"

const Exhibit = () => {
  const { id } = useParams();
  const location = useLocation();
  const { language, dir, t } = useLanguage();
  const { exhibit, loading, error } = useExhibitData(id, language);
  const { trackExhibitView } = useAnalyticsTracking();
  const { prevExhibit, nextExhibit, tourId } = useExhibitNavigation(id);
  
  // Extract referrer information from location state
  const fromTour = location.state?.from || '';
  const viewStartTime = useRef<number>(Date.now());
  
  useEffect(() => {
    // Log navigation state for debugging
    console.log('Exhibit page - Location state:', location.state);
    console.log('Exhibit page - From tour:', fromTour);
  }, [location.state, fromTour]);

  // Track exhibit view
  useEffect(() => {
    if (exhibit && id) {
      viewStartTime.current = Date.now();
      return () => {
        // Track view duration when leaving
        const duration = Math.round((Date.now() - viewStartTime.current) / 1000);
        trackExhibitView(id, duration);
      };
    }
  }, [exhibit, id, trackExhibitView]);

  if (loading) {
    return (
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="mb-4">
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-6 w-1/2 mt-2" />
        </div>
        <Skeleton className="h-64 w-full rounded-xl" />
        <Skeleton className="h-4 w-full mt-4" />
        <Skeleton className="h-4 w-5/6 mt-2" />
        <Skeleton className="h-4 w-3/4 mt-2" />
      </div>
    );
  }

  if (error || !exhibit) {
    return (
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="text-center">
          <h1 className="text-2xl font-bold">{t('exhibit_not_found')}</h1>
          <p>{t('exhibit_not_found_description')}</p>
        </div>
      </div>
    );
  }

  const { title, subtitle, description, images, audio, videos, numberCode, hasAudio, hasVideo } = exhibit;
  // Safely access exhibit location with default value
  const exhibitLocation = exhibit.location || null;

  // Filter audio tracks to only show the one matching current language
  const filteredAudio = audio.filter(track => {
    // Show track if it matches current language
    if (track.language === language) {
      return true;
    }
    // For English, also show tracks with no language specified
    if (language === 'en' && (!track.language || track.language === '')) {
      return true;
    }
    return false;
  });

  console.log('=== EXHIBIT PAGE AUDIO FILTERING ===');
  console.log('Current language:', language);
  console.log('All audio tracks:', audio);
  console.log('Filtered audio tracks:', filteredAudio);
  console.log('Will show audio section:', hasAudio && filteredAudio.length > 0);

  return (
    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 pt-6">
      
      {/* Header Section */}
      <div className="mb-4">
        <div className="flex items-start gap-4">
          <span className="text-xl font-medium text-gray-600 flex-shrink-0">{numberCode}</span>
          <h1 className="text-2xl font-bold leading-tight whitespace-pre-line">
            {title}
          </h1>
        </div>
        {subtitle && <h2 className="text-xl text-gray-700 mt-1">{subtitle}</h2>}
      </div>
      
      {/* Image Section */}
      <ExhibitImage images={images.map(img => img.url)} alt={title} />
      
      {/* Audio Section - Only show if we have filtered audio for current language */}
      {hasAudio && filteredAudio.length > 0 && (
        <div className="mt-6">
          {filteredAudio.map((track, index) => {
            console.log(`Rendering filtered audio track ${index}:`, track);
            return (
              <ExhibitAudio key={index} track={track} language={language} />
            );
          })}
        </div>
      )}
      
      {hasVideo && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold mb-2">{t('video')}</h3>
          {videos.map((video, index) => (
            <ExhibitVideo key={index} video={video} />
          ))}
        </div>
      )}
      
      {/* Description Section - Now at the bottom after media */}
      <div className="mt-6">
        <ExhibitDetails 
          description={description} 
          language={language}
          location={exhibitLocation}
          hasVideo={false}  // We're handling videos separately above
        />
      </div>
      
      <ExhibitNavigation 
        prevExhibit={prevExhibit} 
        nextExhibit={nextExhibit} 
        dir={dir} 
        t={t} 
        tourId={tourId}
      />
    </div>
  );
}

export default Exhibit;
